import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { getMediaDetail } from './api';
import { patchMedia, getMediaUsage, deleteMedia } from './apiMedia';
import Spinner from './ui/Spinner';
import PageHeader from './ui/PageHeader';
import Alert from './ui/Alert';
import Badge from './ui/Badge';
import ConfirmDialog from './ui/ConfirmDialog';
import TagInput from './media/TagInput';
import { useToast } from './Toast';
import { mediaLabel, mediaState, normalizeTags } from './mediaHelpers';

/* The editable subset — everything else on the doc is written by the resize
   webhook and must not be round-tripped through a PATCH. */
const EDITABLE = ['name', 'description', 'tags', 'source', 'contributor_name', 'editorial_only'];

const formOf = (media) => ({
  name: media.name || '',
  description: media.description || '',
  tags: normalizeTags(media.tags || []),
  source: media.source || '',
  contributor_name: media.contributor_name || '',
  editorial_only: !!media.editorial_only,
});

const sameValue = (a, b) => {
  if (Array.isArray(a) || Array.isArray(b)) {
    return JSON.stringify(a || []) === JSON.stringify(b || []);
  }
  return a === b;
};

const ROLE_LABELS = { main_image: 'Main image', section: 'In a section' };

const ImageDetail = () => {
  const { mediaId } = useParams();
  const navigate = useNavigate();
  const { addToast } = useToast();
  const [media, setMedia] = useState(null);
  const [form, setForm] = useState(null);
  const [baseline, setBaseline] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState('');
  const [usage, setUsage] = useState(null);
  const [usageError, setUsageError] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const loadUsage = useCallback(async () => {
    setUsageError(null);
    try {
      setUsage(await getMediaUsage(mediaId));
    } catch (err) {
      setUsageError(err.response?.data?.detail || 'Could not check where this image is used');
    }
  }, [mediaId]);

  useEffect(() => {
    const fetchMedia = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await getMediaDetail(mediaId);
        setMedia(data);
        setForm(formOf(data));
        setBaseline(formOf(data));
      } catch (err) {
        if (err.response && err.response.status === 401) {
          navigate('/login');
        } else {
          setError(err.response?.data?.detail || 'Failed to fetch media details');
        }
      } finally {
        setLoading(false);
      }
    };
    fetchMedia();
    loadUsage();
  }, [mediaId, navigate, loadUsage]);

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    setCopied(label);
    setTimeout(() => setCopied(''), 2000);
  };

  const dirtyFields = form && baseline ? EDITABLE.filter((k) => !sameValue(form[k], baseline[k])) : [];
  const dirty = dirtyFields.length > 0;
  const inUse = !!(usage && usage.count);

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const handleSave = async () => {
    if (!dirty || saving) return;
    setSaving(true);
    setError(null);
    try {
      const patch = {};
      dirtyFields.forEach((k) => { patch[k] = k === 'tags' ? normalizeTags(form.tags) : form[k]; });
      const updated = await patchMedia(mediaId, patch);
      setMedia(updated);
      setForm(formOf(updated));
      setBaseline(formOf(updated));
      addToast('Media saved', 'success');
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Failed to save media');
      addToast('Could not save media', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => setForm({ ...baseline });

  const handleDelete = async () => {
    setConfirmDelete(false);
    try {
      await deleteMedia(mediaId);
      addToast('Media deleted', 'success');
      navigate('/images');
    } catch (err) {
      if (err.response && err.response.status === 409) {
        const detail = err.response.data?.detail || {};
        setUsage({ pages: detail.pages || [], count: detail.count || 0 });
        setError(detail.message || 'This image is still used on a page.');
      } else {
        const detail = err.response?.data?.detail;
        setError(typeof detail === 'string' ? detail : 'Failed to delete media');
      }
      addToast('Could not delete media', 'error');
    }
  };

  const backLink = (
    <Link className="btn btn-secondary" to="/images">
      ← Back to media
    </Link>
  );

  if (loading) {
    return (
      <div className="page page-narrow">
        <Spinner label="Loading media…" />
      </div>
    );
  }

  if (error && !media) {
    return (
      <div className="page page-narrow">
        <PageHeader title="Media" actions={backLink} />
        <Alert type="error">{error || 'Media item not found.'}</Alert>
      </div>
    );
  }

  if (!media || !form) {
    return (
      <div className="page page-narrow">
        <PageHeader title="Media" actions={backLink} />
        <Alert type="error">Media item not found.</Alert>
      </div>
    );
  }

  // Label/value rows inside the read-only detail cards.
  const rowStyle = { padding: '8px 0', borderBottom: '1px solid var(--color-border)', display: 'flex', alignItems: 'flex-start', gap: '10px', flexWrap: 'wrap' };
  const labelStyle = { fontWeight: 600, color: 'var(--color-muted)', fontSize: '13px', minWidth: '150px' };
  const Row = ({ label, children }) => (
    <div style={rowStyle}>
      <span style={labelStyle}>{label}</span>
      <div style={{ flex: 1, minWidth: 0, overflowWrap: 'anywhere' }}>{children}</div>
    </div>
  );

  const pathRows = [
    ['Thumbnail', media.thumb_path],
    ['Large (JPEG)', media.large_path_jpeg],
    ['Large (WebP)', media.large_path_webp],
    ['Raw / original', media.raw_path],
  ].filter(([, value]) => value);

  const processing = mediaState(media) === 'processing';

  return (
    <div className="page page-narrow">
      <PageHeader
        title={mediaLabel(media) || 'Media detail'}
        subtitle={media.original_filename && media.name ? media.original_filename : undefined}
        actions={
          <>
            {backLink}
            <button type="button" className="btn btn-primary" onClick={handleSave} disabled={!dirty || saving}>
              {saving ? 'Saving…' : dirty ? 'Save changes' : 'Saved'}
            </button>
          </>
        }
      />

      <Alert type="error" onDismiss={() => setError(null)}>{error}</Alert>
      {processing && (
        <Alert type="info">Thumbnails are still being generated for this image.</Alert>
      )}

      {/* Image preview */}
      <div className="card" style={{ textAlign: 'center', backgroundColor: 'var(--color-bg)' }}>
        {media.filepath ? (
          <img
            src={media.filepath}
            alt={mediaLabel(media) || 'Image'}
            style={{ maxWidth: '100%', maxHeight: '500px', objectFit: 'contain', borderRadius: 'var(--radius)' }}
            onError={(e) => { e.target.style.display = 'none'; }}
          />
        ) : (
          <div className="muted" style={{ padding: '40px' }}>No preview available</div>
        )}
      </div>

      {/* Editable metadata */}
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">Details</h3>
          {dirty && <Badge state="warn">Unsaved changes</Badge>}
        </div>

        <div className="field">
          <label htmlFor="media-name">Name</label>
          <input
            id="media-name"
            className="input"
            value={form.name}
            onChange={(e) => set('name', e.target.value)}
          />
        </div>

        <div className="field">
          <label htmlFor="media-description">Alt text / description</label>
          <textarea
            id="media-description"
            className="textarea"
            value={form.description}
            onChange={(e) => set('description', e.target.value)}
            placeholder="What the image shows — used as alt text on the site"
          />
        </div>

        <div className="field">
          <label htmlFor="media-tags">Tags</label>
          <TagInput id="media-tags" value={form.tags} onChange={(tags) => set('tags', tags)} />
        </div>

        <div className="field-row">
          <div className="field">
            <label htmlFor="media-source">Source</label>
            <input
              id="media-source"
              className="input"
              value={form.source}
              onChange={(e) => set('source', e.target.value)}
            />
          </div>
          <div className="field">
            <label htmlFor="media-contributor">Contributor</label>
            <input
              id="media-contributor"
              className="input"
              value={form.contributor_name}
              onChange={(e) => set('contributor_name', e.target.value)}
            />
          </div>
        </div>

        <div className="field field-inline">
          <input
            id="media-editorial"
            type="checkbox"
            checked={form.editorial_only}
            onChange={(e) => set('editorial_only', e.target.checked)}
          />
          <label htmlFor="media-editorial">Editorial use only</label>
        </div>

        <div className="toolbar" style={{ justifyContent: 'flex-end' }}>
          {dirty && (
            <button type="button" className="btn btn-ghost" onClick={handleReset} disabled={saving}>
              Discard changes
            </button>
          )}
          <button type="button" className="btn btn-primary" onClick={handleSave} disabled={!dirty || saving}>
            {saving ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </div>

      {/* Read-only facts */}
      <div className="card">
        <h3 className="card-title">Identifiers</h3>

        <Row label="ID">
          <code className="mono">{media.id}</code>{' '}
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => copyToClipboard(media.id, 'id')}>
            {copied === 'id' ? 'Copied' : 'Copy'}
          </button>
        </Row>

        {media.original_filename && <Row label="Original filename">{media.original_filename}</Row>}
        {media.media_type && <Row label="Type">{media.media_type}</Row>}
        {media.collections && media.collections.length > 0 && (
          <Row label="Collections">{media.collections.join(', ')}</Row>
        )}
      </div>

      {/* Usage */}
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">Used on</h3>
          <button type="button" className="btn btn-ghost btn-sm" onClick={loadUsage}>Refresh</button>
        </div>
        {usageError ? (
          <Alert type="warning">{usageError}</Alert>
        ) : !usage ? (
          <Spinner size={24} label="Checking usage…" />
        ) : usage.count === 0 ? (
          <p className="muted" style={{ margin: 0 }}>No page references this image.</p>
        ) : (
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Page</th>
                  <th>Site</th>
                  <th>Used as</th>
                </tr>
              </thead>
              <tbody>
                {usage.pages.map((row) => (
                  <tr key={`${row.domain_id}:${row.slug}:${row.role}`}>
                    <td>
                      <Link to={`/domain/${encodeURIComponent(row.domain_id)}/pages/${encodeURIComponent(row.slug)}/edit`}>
                        {row.title || row.slug}
                      </Link>
                      <div className="muted small">{row.slug}</div>
                    </td>
                    <td className="muted small">{row.hostname}</td>
                    <td>
                      <Badge state={row.role === 'main_image' ? 'info' : 'plain'}>
                        {ROLE_LABELS[row.role] || row.role}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* GCS paths */}
      {pathRows.length > 0 && (
        <div className="card">
          <h3 className="card-title">Storage paths</h3>
          {pathRows.map(([label, value]) => (
            <Row key={label} label={label}>
              <code className="mono">{value}</code>{' '}
              <button type="button" className="btn btn-ghost btn-sm" onClick={() => copyToClipboard(value, label)}>
                {copied === label ? 'Copied' : 'Copy'}
              </button>
            </Row>
          ))}
        </div>
      )}

      {/* Delete */}
      <div className="card">
        <h3 className="card-title">Delete</h3>
        <p className="muted" style={{ marginTop: 0 }}>
          {inUse
            ? `This image is used on ${usage.count} page${usage.count === 1 ? '' : 's'}. Remove it there first — deleting it would break those pages.`
            : 'Removes the media record and its files from storage. This cannot be undone.'}
        </p>
        <button
          type="button"
          className="btn btn-danger"
          onClick={() => setConfirmDelete(true)}
          disabled={inUse || !usage}
          title={inUse ? 'In use — cannot be deleted' : undefined}
        >
          Delete image
        </button>
      </div>

      <ConfirmDialog
        open={confirmDelete}
        title="Delete this image?"
        message={`${mediaLabel(media)} and its generated thumbnails will be removed from storage. This cannot be undone.`}
        confirmLabel="Delete"
        danger
        onConfirm={handleDelete}
        onCancel={() => setConfirmDelete(false)}
      />
    </div>
  );
};

export default ImageDetail;
